import React from 'react';
import Icon from '@mdi/react';
import { mdiBankOutline } from '@mdi/js';
import { formatCurrencyFromMinor } from '../../utils/currency';
import './PendingPayoutsCard.css';

/**
 * PendingPayoutsCard — Shows upcoming scheduled payout info.
 *
 * `balances` is the per-currency array from the balance API (one entry per currency a payout is
 * pending in). We show every currency, ordered with the org's base currency first, so a
 * travelling instructor sees all pending payouts instead of one arbitrary currency. nextPayoutDate
 * (scheduledPayoutDate on transactions) reflects the actual Stripe payout schedule.
 */
function PendingPayoutsCard({ balances, baseCurrency }) {

  const formatPayoutDate = (dateValue) => {
    if (!dateValue) return null;
    return new Date(dateValue).toLocaleDateString('en-AU', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  };

  const list = (Array.isArray(balances) ? balances : []).filter(b => b && b.amount);
  // Base currency first, then largest pending amount.
  const sorted = [...list].sort((a, b) => {
    const aBase = a.currency?.toUpperCase() === baseCurrency?.toUpperCase() ? 1 : 0;
    const bBase = b.currency?.toUpperCase() === baseCurrency?.toUpperCase() ? 1 : 0;
    if (aBase !== bBase) return bBase - aBase;
    return (b.amount || 0) - (a.amount || 0);
  });
  // Soonest scheduled transfer date across the pending currencies.
  const nextDate = sorted.map(b => b.nextPayoutDate).filter(Boolean).sort()[0] || null;
  const payoutDateLabel = formatPayoutDate(nextDate);

  return (
    <div className="pending-payouts-card">
      <div className="pending-payouts-header">
        <h3 className="pending-payouts-title">Next Payout</h3>
        <div className="pending-payouts-icon">
          <Icon path={mdiBankOutline} size={1.1} />
        </div>
      </div>

      <div className="pending-payouts-amount">
        {sorted.length > 0 ? (
          <>
            {sorted.map(b => (
              <span className="pending-amount-value" key={b.currency}>
                {formatCurrencyFromMinor(b.amount, b.currency)}
              </span>
            ))}
            <span className="pending-amount-label">
              {payoutDateLabel ? `Scheduled for transfer: ${payoutDateLabel}` : 'Scheduled for transfer'}
            </span>
          </>
        ) : (
          <span className="pending-amount-empty">No upcoming payouts</span>
        )}
      </div>
    </div>
  );
}

export default PendingPayoutsCard;
